/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.sprudel.pattern

import io.peekandpoke.klang.common.math.CycleTime

import io.peekandpoke.klang.sprudel.SprudelPattern
import io.peekandpoke.klang.sprudel.SprudelPatternEvent
import io.peekandpoke.klang.sprudel.SprudelVoiceData

/**
 * Pattern that selects from a lookup table based on a selector pattern and flattens with innerJoin.
 *
 * @param selector Pattern providing selection indices/keys
 * @param lookup Map of patterns to select from (keys can be Int for lists or String for maps)
 * @param modulo If true, wrap out-of-bounds indices; if false, clamp them
 * @param extractKey Function to extract the lookup key from a selector event value
 */
internal class PickSqueezePattern(
    private val selector: SprudelPattern,
    private val lookup: Map<Any, SprudelPattern>,
    private val modulo: Boolean,
    private val extractKey: (SprudelVoiceData, Boolean, Int) -> Any?,
) : SprudelPattern {

    override val weight: Double get() = selector.weight
    override val numSteps: Double? get() = selector.numSteps
    override fun estimateCycleDuration(): Double = selector.estimateCycleDuration()

    override fun queryArcContextual(
        from: CycleTime,
        to: CycleTime,
        ctx: SprudelPattern.QueryContext,
    ): List<SprudelPatternEvent> {
        // Query the selector pattern to get selection events
        val selectorEvents = selector.queryArcContextual(from, to, ctx)
        val result = mutableListOf<SprudelPatternEvent>()

        // For each selector event, look up the corresponding pattern and query it
        for (selectorEvent in selectorEvents) {
            // Extract the key/index from the selector event's value
            val key: Any? = extractKey(selectorEvent.data, modulo, lookup.size)

            // Get the pattern from lookup
            val selectedPattern = if (key != null) lookup[key] else null
            if (selectedPattern == null) continue

            // Determine the intersection of the query arc and the selector event
            val intersectStart = from.coerceAtLeast(selectorEvent.part.begin)
            val intersectEnd = to.coerceAtMost(selectorEvent.part.end)

            if (intersectEnd <= intersectStart) continue

            // Squeeze the inner pattern into the selector event's duration.
            // We map the outer time 't' to inner time 't'' such that:
            // selectorEvent.part.begin -> 0
            // selectorEvent.part.end -> 1 (assuming 1 cycle for inner pattern)

            val duration = selectorEvent.part.duration
            if (duration == CycleTime.ZERO) continue
            val durCycles = duration.toCycles()

            val innerStart = (intersectStart - selectorEvent.part.begin).divBy(durCycles)
            val innerEnd = (intersectEnd - selectorEvent.part.begin).divBy(durCycles)

            val innerEvents = selectedPattern.queryArcContextual(innerStart, innerEnd, ctx)

            // Map inner events back to outer time
            for (innerEvent in innerEvents) {
                val scaledPart = innerEvent.part.scale(durCycles).shift(selectorEvent.part.begin)
                val scaledWhole = innerEvent.whole.scale(durCycles).shift(selectorEvent.part.begin)

                result.add(innerEvent.copy(part = scaledPart, whole = scaledWhole))
            }
        }

        return result
    }
}
